import { usePropertyStructureType } from '../../../queries/PropertyFields/TopLevelFields';
import { usePropertyGeneralContractorWillWork, usePropertyPreSold } from '../../../queries/PropertyFields/NonRentalFlagFields';
import { usePropertyContactIsPrimary } from '../../../queries/PropertyFields/NonRentalContactFields';
import { usePropertySpecSheetFile } from '../../../queries/PropertyFileFields/NonRentalFiles';

import { ShowIf } from '../../components/ShowIf';
import { ConstructionStarted } from '../components/ConstructionStarted';
import { GeneralContractor } from '../components/GeneralContractor';
import { GeneralContractorCompany } from '../components/GeneralContractorCompany';
import { LandSize } from '../components/LandSize';
import { LegalDescription } from '../components/LegalDescription';
import { OnsitePropertyContact } from '../components/OnsitePropertyContact';
import { PreSold } from '../components/PreSold';
import { StructureType } from '../components/StructureType';
import { PropertyAddress } from '../components/PropertyAddress/PropertyAddress';
import { ExpectedListingPrice } from '../components/ExpectedListingPrice';
import { Units } from '../components/Units';
import { OnsitePropertyContactInformation } from '../components/OnsitePropertyContactInformation/OnsitePropertyContactInformation';
import { GeneralContractorFile } from '../../components/FileOrFormQuestions/questions/GeneralContractorFile';
import { BudgetFile } from '../../components/FileOrFormQuestions/questions/BudgetFile';
import { PlansFile } from '../../components/FileOrFormQuestions/questions/PlansFile';
import { SpecSheetFile } from '../../components/FileOrFormQuestions/questions/SpecSheetFile';
import { SurveyFile } from '../../components/FileOrFormQuestions/questions/SurveyFile';
import { PreSaleContractFile } from '../../components/FileOrFormQuestions/questions/PreSaleContractFile';
import { ProvideLaterAdvisory } from '../components/ProvideLaterAdvisory';

export interface ConstructionProps {
    disableEditFormOptionBudget: boolean;
    disableEditFormOptionSpecSheet: boolean;
    propertyGuid: string;
    isReview?: boolean;
}

export const Construction: React.FC<ConstructionProps> = ({
    disableEditFormOptionBudget,
    disableEditFormOptionSpecSheet,
    propertyGuid,
    isReview = false,
}) => {
    const [structureType] = usePropertyStructureType(propertyGuid);
    const [generalContractorWillWork] = usePropertyGeneralContractorWillWork(propertyGuid);
    const [contactIsPrimary] = usePropertyContactIsPrimary(propertyGuid);
    const [preSold] = usePropertyPreSold(propertyGuid);
    const { handleMode: specSheetHandle } = usePropertySpecSheetFile(propertyGuid);

    return (
        <>
            <StructureType
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <ShowIf showIf={structureType === '5+ Unit'}>
                <Units
                    required={true}
                    propertyGuid={propertyGuid}
                    minimumUnits={5}
                    isReview={isReview}
                />
            </ShowIf>
            <PropertyAddress
                propertyGuid={propertyGuid}
                readOnly={false}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={!isReview}>
                <br />
                <br />
                <div className='pageSubHeader'>Additional Information</div>
            </ShowIf>
            <LegalDescription
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <LandSize
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <GeneralContractor
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={generalContractorWillWork}>
                <br />
                <br />
                <div className='pageSubHeader'>General Contractor</div>
                <GeneralContractorCompany
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
                <GeneralContractorFile
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>

            <ShowIf showIf={!generalContractorWillWork}>
                <OnsitePropertyContact
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <ShowIf showIf={!generalContractorWillWork && contactIsPrimary === false}>
                <OnsitePropertyContactInformation
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <ShowIf showIf={!isReview}>
                <br />
                <br />
                <div className='pageSubHeader'>Additional Files</div>
            </ShowIf>
            <BudgetFile
                disableEditFormOptionBudget={disableEditFormOptionBudget}
                required={true}
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <PlansFile
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <SpecSheetFile
                propertyGuid={propertyGuid}
                disableEditFormOptionSpecSheet={disableEditFormOptionSpecSheet}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={!isReview && specSheetHandle === 'willnotprovide'}>
                <p style={{ color: 'red', marginLeft: 20 }}>
                    NOTE: Without this detail, the appraiser is likely to assume the lowest grade materials.
                </p>
            </ShowIf>
            <SurveyFile
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <PreSold
                required={true}
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <ShowIf showIf={preSold}>
                <PreSaleContractFile
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <br />
            <ShowIf showIf={!preSold}>
                <ExpectedListingPrice
                    propertyGuid={propertyGuid}
                    required={true}
                    structureType={structureType}
                    isReview={isReview}
                />
            </ShowIf>
            <ProvideLaterAdvisory
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <ConstructionStarted
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
        </>
    );
};
